import React, { useContext, useEffect, useMemo, useState } from "react";
import ProfileCard from "../../../common/ProfileCard/ProfileCard";
import { ApiContext } from "../../../context/MyContext";
import { hasButtonPermission } from "../../../utils/accessControl";
import { PROFILE_PERMISSION_PAGE, PROFILE_TABS } from "./profileTabs";
import { serializeUserProfileKey } from "../../../utils/listCache/caches";

/** 'app' shows every permitted tab and the profile pill; 'prospect' hides app-only tabs and the pill. */
export const PROFILE_VIEW_MODE = Object.freeze({
  APP: "app",
  PROSPECT: "prospect",
});

const tabClass = (active) =>
  `py-2.5 px-5 me-2 mb-1 text-gray-600 focus:outline-none font-small ${active ? "bg-input-bg text-color-text" : ""}`;

/**
 * Client profile screen: ProfileCard pill + tab bar + active tab. Used by the
 * Users profile route and the Lead page.
 * @param {{ profileId: string, mode?: 'app'|'prospect',
 *   leadingTabs?: { id: string, label: string, render: () => React.ReactNode }[],
 *   defaultTab?: string, showAlert?: (message: string) => void, onSubscriptionsChanged?: () => void,
 *   showCoachingTabs?: boolean }} props  showCoachingTabs=false drops the trackers and coach Notes.
 */
export default function UserProfileView({
  profileId,
  mode = PROFILE_VIEW_MODE.APP,
  leadingTabs = [],
  defaultTab,
  showAlert,
  onSubscriptionsChanged,
  showCoachingTabs = true,
}) {
  const { userId, setUserId, employeeData, departmentsWithRoles } =
    useContext(ApiContext);
  const isApp = mode === PROFILE_VIEW_MODE.APP;

  const tabs = useMemo(() => {
    const allowed = PROFILE_TABS.filter(
      (tab) =>
        (isApp || !tab.appOnly) &&
        (showCoachingTabs || !tab.coaching) &&
        (!tab.permission ||
          hasButtonPermission(
            tab.permission,
            PROFILE_PERMISSION_PAGE,
            employeeData,
            departmentsWithRoles,
          )),
    );
    return [...leadingTabs, ...allowed];
  }, [isApp, showCoachingTabs, leadingTabs, employeeData, departmentsWithRoles]);

  const [selectedTab, setSelectedTab] = useState(defaultTab || tabs[0]?.id);
  const activeTab = tabs.find((tab) => tab.id === selectedTab) || tabs[0];

  // Tab components (Activity, Notes) read the client id from context, so keep it on this profile.
  useEffect(() => {
    if (isApp && profileId && userId !== profileId) {
      setUserId(profileId);
      try {
        localStorage.setItem("userId", JSON.stringify(profileId));
      } catch {
        // Storage full or blocked (private mode): the in-memory context is enough for this page.
      }
    }
  }, [isApp, profileId, userId, setUserId]);

  if (isApp && !userId) return <div>Loading...</div>;

  return (
    <>
      {isApp ? (
        <ProfileCard profileId={profileId} cacheKey={serializeUserProfileKey(profileId)} enableNotesTab />
      ) : null}
      <div
        role="tablist"
        aria-label="Client profile"
        className="flex flex-wrap space-x-4 mt-4 bg-white p-4 rounded-lg"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === activeTab?.id}
            className={tabClass(tab.id === activeTab?.id)}
            onClick={() => setSelectedTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        className="w-full min-w-0 bg-app-bg rounded-lg p-10 shadow-lg flex items-stretch"
      >
        {activeTab?.render({ profileId, showAlert, onSubscriptionsChanged })}
      </div>
    </>
  );
}
